import React, { type HTMLAttributes } from 'react';
import type { RequestOptions } from 'searchkit';

import {
  SearchkitWrapper,
  SearchResultsHeadless,
  SearchResultsList,
} from '@uncinc/react-searchkit';

import type {
  MetaParagraphsContentTypes,
} from '../../../types/uncinc_profile/paragraphs';
import type {
  SearchAllContentArticleHit,
  SearchAllContentHit,
  SearchAllContentProfileHit,
  SearchAllContentProjectHit,
  SearchAllContentVacancyHit,
} from '../../SearchAllContent/types';

import SearchAllContentFacets from '../../SearchAllContent/Facets';
import SearchAllContentPagination from '../../SearchAllContent/Pagination';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../../config/constants';
import { facetAttributes, resultAttributes } from './config';

import { sortByCustomOrder } from './metaParagraphsHelpers';

import ArticleHit from './Results/ArticleHit';
import ProjectHit from './Results/ProjectHit';
import VacancyHit from './Results/VacancyHit';
import ProfileHit from './Results/ProfileHit';

const RenderItem = (hit: SearchAllContentHit) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { content_type, profile_type } = hit;

  const type = profile_type?.[0] ? profile_type[0] : content_type[0];


  switch (type) {
    case 'article': {
      return <ArticleHit hit={hit as SearchAllContentArticleHit} />;
    }
    case 'project': {
      return <ProjectHit hit={hit as SearchAllContentProjectHit} />;
    }
    case 'vacancy': {
      return <VacancyHit hit={hit as SearchAllContentVacancyHit} />;
    }
    case 'default_profile': {
      return <ProfileHit hit={hit as SearchAllContentProfileHit} />;
    }
    default: {
      return <div />;
    }
  }
};

interface MetaSearchProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  requestOptions: RequestOptions;
  contentType: MetaParagraphsContentTypes;
  sortingList?: number[];
  hitsPerPage: number;
  withFacets?: boolean;
  withPagination?: boolean;
}

const MetaSearch: React.FC<MetaSearchProps> = ({
  requestOptions,
  contentType,
  sortingList = undefined,
  hitsPerPage,
  withFacets = false,
  withPagination = false,
}) => (
  <div className="MetaSearch">
    <SearchkitWrapper
      connection={{
        host: SEARCHKIT_HOST_URL,
      }}
      indexName={APP_OPTIONS?.parameters.searchkitIndexAllContent}
      routing={false}
      hitsPerPage={hitsPerPage}
      requestOptions={requestOptions}
      searchSettings={{
        search_attributes: [],
        result_attributes: resultAttributes,
        facet_attributes: facetAttributes,
        sorting: {
          default: {
            field: (contentType === 'article' || contentType === 'vacancy') ? 'publish_date' : 'created',
            order: 'desc',
          },
        },
      }}
    >

      {withFacets && (
        <SearchAllContentFacets dialogEnabled={false} />
      )}

      <div className="ParagraphMeta__results ParagraphHighlightedSelection__results">
        <SearchResultsHeadless>
          {(hits) => {
            const sortedItems = sortingList ? sortByCustomOrder(hits, sortingList) : hits;

            return (
              <SearchResultsList
                renderItem={RenderItem}
                hits={sortedItems}
              />
            );
          }}
        </SearchResultsHeadless>
      </div>

      {withPagination && (
        <SearchAllContentPagination />
      )}
    </SearchkitWrapper>
  </div>
);

export default MetaSearch;
